import React, { useEffect, useState } from "react";
import { AlertTriangle, Check, Plus, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

const WORKFLOW_LABELS = {
  proposal: "Proposal", budget: "Budget", secretariat_forms: "Secretariat Forms",
  registration_forms: "Registration Forms", committees_agendas: "Committees & Agendas",
  vendors: "Vendors", trophies: "Trophies", collaborations: "Collaborations",
  brochures: "Brochures", social_media: "Social Media Calendar",
  cash_prizes: "Cash Prizes", attendance: "Attendance", marking_sheet: "Marking Sheet",
  art_craft: "Art & Craft", merchandise: "Merchandise",
};

export default function QueriesTab({ event }) {
  const { user } = useAuth();
  const [queries, setQueries] = useState([]);
  const [response, setResponse] = useState({});
  const [showRaise, setShowRaise] = useState(false);

  const load = async () => setQueries((await api.get(`/events/${event.event_id}/queries`)).data);
  useEffect(() => { load(); }, [event.event_id]);

  const respond = async (q) => {
    const txt = response[q.query_id] || "";
    if (!txt) return toast.error("Type a response first");
    await api.patch(`/queries/${q.query_id}`, { pm_response: txt, status: "resolved" });
    toast.success("Response sent");
    setResponse({ ...response, [q.query_id]: "" });
    load();
  };

  const reopen = async (q) => { await api.patch(`/queries/${q.query_id}`, { status: "open" }); load(); };

  const canRaise = user.role === "admin" || user.role === "regional_pm";

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-xl">Queries from Regional PM / Admin</h3>
          <p className="text-sm text-slate-500">
            {user.role === "project_manager"
              ? "When RPM or Admin flags something on your event, you'll see it here."
              : "Raise a query to flag anything the PM should look into."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="role-pill bg-amber-100 text-amber-700">{queries.filter((q) => q.status === "open").length} open</span>
          {canRaise && (
            <button
              onClick={() => setShowRaise(true)}
              className="btn-primary text-sm py-2"
              data-testid="raise-query-btn"
            >
              <Plus className="w-4 h-4" /> Raise query
            </button>
          )}
        </div>
      </div>
      <div className="space-y-3">
        {queries.length === 0 && <div className="dashboard-card p-12 text-center text-slate-400">No queries raised on this event.</div>}
        {queries.map((q) => (
          <div key={q.query_id} className={`dashboard-card p-5 ${q.status === "open" ? "border-amber-200 bg-amber-50/30" : "border-emerald-200 bg-emerald-50/20"}`} data-testid={`query-${q.query_id}`}>
            <div className="flex items-start gap-3">
              {q.status === "open" ? <AlertTriangle className="w-5 h-5 text-amber-600 mt-1" /> : <Check className="w-5 h-5 text-emerald-600 mt-1" />}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold">{q.title}</div>
                  <span className={`role-pill ${q.status === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{q.status}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Raised by {q.raised_by_name} ({q.raised_by_role}) · step: {q.step_key || "general"}</div>
                <p className="text-sm text-slate-700 mt-2">{q.message}</p>
                {q.pm_response && <div className="mt-3 p-3 rounded-lg bg-white border border-emerald-100 text-sm"><b>PM response:</b> {q.pm_response}</div>}

                {q.status === "open" && (user.role === "project_manager" || user.role === "admin") && (
                  <div className="flex gap-2 mt-3">
                    <input className="input-pill text-sm py-2" placeholder="Respond to this query" value={response[q.query_id] || ""} onChange={(e) => setResponse({ ...response, [q.query_id]: e.target.value })} data-testid={`query-resp-${q.query_id}`} />
                    <button onClick={() => respond(q)} className="btn-primary py-2 text-sm">Send &amp; resolve</button>
                  </div>
                )}
                {q.status === "resolved" && q.raised_by === user.user_id && (
                  <button onClick={() => reopen(q)} className="text-xs text-slate-500 underline mt-2">Reopen</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showRaise && (
        <RaiseQueryModal
          event={event}
          onClose={() => setShowRaise(false)}
          onSaved={() => { setShowRaise(false); load(); }}
        />
      )}
    </div>
  );
}

function RaiseQueryModal({ event, onClose, onSaved }) {
  const [form, setForm] = useState({ step_key: "", title: "", message: "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) { toast.error("Title and message required"); return; }
    setSaving(true);
    try {
      await api.post(`/events/${event.event_id}/queries`, form);
      toast.success("Query raised — PM will be notified");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="raise-query-modal"
      >
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase font-bold text-amber-600">Query</div>
            <h3 className="font-display font-black text-xl">Raise a query</h3>
            <div className="text-xs text-slate-500 mt-0.5">Event · {event.name}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100 shrink-0"
            data-testid="raise-query-close"
          ><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-3">
          <select
            className="input-pill"
            value={form.step_key}
            onChange={(e) => setForm({ ...form, step_key: e.target.value })}
            data-testid="query-step"
          >
            <option value="">Step (optional)</option>
            {Object.entries(WORKFLOW_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input
            required
            placeholder="Query title"
            className="input-pill"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="query-title"
          />
          <textarea
            required
            rows={4}
            placeholder="What needs the PM's attention?"
            className="input-pill resize-none"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="query-message"
          />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button
              type="submit"
              disabled={saving || !form.title.trim() || !form.message.trim()}
              className="btn-primary px-5 disabled:opacity-50"
              data-testid="query-submit"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send to PM</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
